import { useState, useEffect } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Badge } from '@/components/shadcn-ui/badge';
import { DataTable } from '@/components/DataTable';
import { AdminChatHistory } from '@/components/chat/AdminChatHistory';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle } from 'lucide-react';

const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GuestChats = () => {
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
    const [selectedGuest, setSelectedGuest] = useState(null);

    const fetchChats = async (page = 1) => {
        setLoading(true);
        try {
            const { data } = await appAxios.get('/Admin/v1.0/get-chats-of-guest', { params: { page, limit: 10 } });
            setChats(data.data.chats || []);
            setPagination(data.data.pagination);
        } catch {
            toast.error('Failed to fetch guest chats');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchChats(1); }, []);

    const columns = [
        {
            key: 'guestId',
            label: 'Guest ID',
            cellClassName: 'font-mono text-sm',
            render: value => value.slice(13) || 'N/A',
        },
        {
            key: 'count',
            label: 'Total Messages',
            render: value => <Badge variant="outline">{value ?? 0} messages</Badge>,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <Button size="sm" variant="outline" onClick={() => setSelectedGuest(row)}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat History
                </Button>
            ),
        },
    ];

    if (selectedGuest) {
        const firstMsg = selectedGuest.chatMessages?.[0];
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => setSelectedGuest(null)} className="border rounded-full">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight font-mono">{selectedGuest.guestId}</h1>
                        <p className="text-sm text-muted-foreground">
                            {selectedGuest.count} messages · {firstMsg ? formatDate(firstMsg.createdAt) : 'N/A'}
                        </p>
                    </div>
                    <div className="ml-auto">
                        <Badge variant="outline" className="rounded-md">{selectedGuest.count} messages</Badge>
                    </div>
                </div>
                <div className="rounded-lg border p-4 max-h-[70vh] overflow-y-auto no-scrollbar">
                    <AdminChatHistory chatHistory={selectedGuest.chatMessages} />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Guest Chats</h1>
                <p className="text-muted-foreground">View chat history from all guest users</p>
            </div>
            <DataTable
                data={chats}
                columns={columns}
                loading={loading}
                searchable={false}
                pagination={pagination}
                onPageChange={page => fetchChats(page)}
                emptyMessage="No guest chats found"
            />
        </div>
    );
};

export default GuestChats;
